import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/common/button/Button";
import DataTable, {
  Column,
} from "../../../components/common/data-table/DataTable";
import Filters from "../filters/Filters";
import styles from "./PayoutReport.module.scss";
import AuditService, {
  AuditFilterParams,
} from "../../../api/services/audit.service";
import { useCurrencies } from "../../../context/CurrenciesContext";

interface PayoutRow {
  store_id: number;
  store_name: string;
  gmv: number;
  platform_profit: number;
  tax: number;
  platform_margin_net: number;
  platform_margin_correct: number;
  store_payout: number;
  order_count: number;
  paid_to_store: number;
  not_yet_paid: number;
  store_remaining: number;
}

const PayoutReport = () => {
  const { t } = useTranslation("translation");
  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const defaultEnd = today.toISOString().slice(0, 10);
  const [storeId, setStoreId] = useState("");
  const [storeName, setStoreName] = useState("");
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { currencyId, currency } = useCurrencies();

  const formatMoney = (val?: number) =>
    `${(val ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency || ""}`;

  const columns: Column[] = useMemo(
    () => [
      { key: "store_name", header: t("payout_report.table.store") as string },
      { key: "order_count", header: t("payout_report.table.orders") as string },
      {
        key: "gmv",
        header: t("payout_report.table.gmv") as string,
        render: formatMoney,
      },
      {
        key: "platform_profit",
        header: t("payout_report.table.platform_profit") as string,
        render: formatMoney,
      },
      {
        key: "tax",
        header: t("payout_report.table.tax") as string,
        render: formatMoney,
      },
      {
        key: "platform_margin_net",
        header: t("payout_report.table.platform_margin_net") as string,
        render: formatMoney,
      },
      {
        key: "platform_margin_correct",
        header: t("payout_report.table.platform_margin_correct") as string,
        render: formatMoney,
      },
      {
        key: "store_payout",
        header: t("payout_report.table.store_payout") as string,
        render: formatMoney,
      },
      {
        key: "paid_to_store",
        header: t("payout_report.table.paid_to_store") as string,
        render: formatMoney,
      },
      {
        key: "not_yet_paid",
        header: t("payout_report.table.not_yet_paid") as string,
        render: formatMoney,
      },
      {
        key: "store_remaining",
        header: t("payout_report.table.store_remaining") as string,
        render: formatMoney,
      },
    ],
    [t],
  );

  const fetchData = async (params: AuditFilterParams) => {
    setLoading(true);
    try {
      const res = await AuditService.getPayoutReport(params);
      setRows(res.data?.data || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    const params: AuditFilterParams = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (storeId) params.store_id = storeId;
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
  };

  const handleReset = () => {
    setStoreId("");
    setStoreName("");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    const params: AuditFilterParams = {};
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
  };

  const handleExport = async () => {
    const params: AuditFilterParams = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (storeId) params.store_id = storeId;
    try {
      setExporting(true);
      const res = await AuditService.exportPayoutReportCsv(params);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const storeSegment = storeName
        ? storeName.replace(/\s+/g, "-")
        : "all-stores";
      const dateSegment =
        startDate && endDate ? `${startDate}_to_${endDate}` : "all-dates";
      link.setAttribute(
        "download",
        `payout-report_${storeSegment}_${dateSegment}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // swallow for now; can add toast if desired
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const params: AuditFilterParams = {};
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyId]);

  return (
    <>
      <Filters
        storeId={storeId}
        startDate={startDate}
        endDate={endDate}
        onChangeStore={(id, name) => {
          setStoreId(id);
          setStoreName(name || "");
        }}
        onChangeStartDate={setStartDate}
        onChangeEndDate={setEndDate}
        onApply={handleApply}
        onReset={handleReset}
      />
      <div className={styles.payout}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {t("payout_report.title") as string}
            </h2>
            <p className={styles.subtitle}>
              {t("payout_report.subtitle") as string}
            </p>
          </div>
          <Button className={styles.export_btn} onClick={handleExport}>
            {exporting
              ? (t("payout_report.exporting") as string)
              : (t("payout_report.export_button") as string)}
          </Button>
        </div>
        <DataTable
          data={rows}
          columns={columns}
          no_borders
          className={styles.table}
          loading={loading}
        />
      </div>
    </>
  );
};

export default PayoutReport;
